/**
 * Family-warm email notification dispatcher.
 *
 * This is the single client-side entrypoint for triggering KinsCircle
 * activity emails (memory uploaded, new family member joined, etc.).
 *
 * RULES THIS FILE OBEYS (per the Resend-only email pipeline):
 *   - We do NOT introduce a new email backend. All outgoing email goes
 *     through the existing `send-family-notification` edge function,
 *     which posts to the Resend HTTP API using RESEND_API_KEY.
 *   - We do NOT add or modify any database schema beyond the additive
 *     `email_prefs` / `email_log` tables.
 *   - We do NOT change auth, uploads, family-tree logic, or routing.
 *   - All edge-function failures are SILENT for the user — notifications
 *     are best-effort. The core action (uploading a memory, adding a
 *     family member, etc.) is never blocked or rolled back by an email
 *     error.
 *   - The actor (user who performed the action) is excluded server-side.
 *     The actor's local pref check is a quick early-exit only — it never
 *     bypasses the server-side dedupe / opt-out / no-self-notify guards.
 *
 * The edge function is responsible for:
 *   - Resolving recipients from `family_graph_members`
 *   - Looking up emails (profiles + auth.users)
 *   - De-duping by email
 *   - Skipping the actor
 *   - Skipping users with `email_prefs.family_activity_emails = false`
 *   - Sending one Resend email per unique recipient
 *   - Logging to `email_log`
 */

import { supabase } from '@/lib/supabase';
import {
  EmailChannel,
  isChannelEnabled,
} from '@/lib/emailPrefs';

export type NotificationType =
  | 'member_joined'         // Someone joined / claimed a profile in the tree
  | 'memory_uploaded'       // A new photo/video/memory/story/life event added
  | 'memory_comment'        // (legacy — kept for backward-compat callers)
  | 'memory_reaction'       // (legacy)
  | 'birthday_reminder'     // (legacy)
  | 'profile_claimed';      // (legacy alias of member_joined)

interface BaseRecipient {
  /** Optional auth user id of a single explicit recipient (legacy path). */
  recipientUserId?: string | null;
  /** Optional explicit address (legacy path, used by sample-digest preview). */
  recipientEmail?: string | null;
  /** Recipient's display name, for greeting copy (legacy path). */
  recipientName?: string | null;
}

interface NotifyArgs extends BaseRecipient {
  type: NotificationType;
  /**
   * Family-tree owner whose graph this event happened in. When provided,
   * the edge function will resolve the recipient list server-side from
   * `family_graph_members.owner_user_id`. This is the preferred path for
   * the new family-activity emails.
   */
  ownerUserId?: string | null;
  /**
   * The user who performed the action — they will be EXCLUDED from the
   * recipient list server-side. (Belt & suspenders: we also short-circuit
   * locally if the actor has the channel turned off.)
   */
  actorUserId?: string | null;
  /** Display name of the actor for email copy. */
  actorName?: string | null;
  /** Free-form payload describing the event, shaped by the edge function. */
  payload?: Record<string, unknown>;
}

const channelForType = (type: NotificationType): EmailChannel => {
  switch (type) {
    case 'member_joined':
    case 'profile_claimed':
      return 'important';
    case 'birthday_reminder':
      return 'birthdays';
    case 'memory_uploaded':
    case 'memory_comment':
    case 'memory_reaction':
    default:
      return 'memory_activity';
  }
};

/**
 * Send a family activity notification. Always safe to call — returns
 * `{ ok: false }` instead of throwing on any failure, and never blocks
 * the calling action.
 *
 * Two modes:
 *   1. owner-mode (preferred for memory_uploaded / member_joined): pass
 *      `ownerUserId` + `actorUserId`. Edge function resolves recipients.
 *   2. single-recipient mode (legacy): pass `recipientUserId` or
 *      `recipientEmail`. Edge function emails just that one address.
 */
export const sendFamilyNotification = async (
  args: NotifyArgs
): Promise<{ ok: boolean; skipped?: boolean; reason?: string }> => {
  // Quick local pref check for the ACTOR — if the actor opted out of all
  // activity emails locally, that doesn't stop notifications to OTHER
  // recipients (their server-side prefs are checked there). We only use
  // the local check to decide whether the per-recipient fall-through
  // single-email mode should fire.
  if (!args.ownerUserId) {
    const channel = channelForType(args.type);
    const userId = args.recipientUserId ?? null;
    if (!isChannelEnabled(userId, channel)) {
      return { ok: false, skipped: true, reason: 'channel_disabled' };
    }
    if (!args.recipientUserId && !args.recipientEmail) {
      return { ok: false, skipped: true, reason: 'no_recipient' };
    }
  } else if (!args.actorUserId) {
    return { ok: false, skipped: true, reason: 'no_actor' };
  }

  try {
    const { data, error } = await supabase.functions.invoke(
      'send-family-notification',
      {
        body: {
          type: args.type,
          ownerUserId: args.ownerUserId ?? null,
          actorUserId: args.actorUserId ?? null,
          actorName: args.actorName ?? null,
          recipientUserId: args.recipientUserId ?? null,
          recipientEmail: args.recipientEmail ?? null,
          recipientName: args.recipientName ?? null,
          payload: args.payload ?? {},
        },
      }
    );
    if (error) {
      // Most likely cause: the edge function isn't deployed yet OR a
      // transient network blip. Log at debug level — never surface to
      // the user, never throw.
      console.debug(
        '[emailNotifications] send-family-notification error:',
        error.message,
      );
      return { ok: false, reason: 'edge_function_error' };
    }
    // Honor structured errors from the edge function (e.g. missing
    // RESEND_API_KEY) without crashing.
    if (data && (data as any).ok === false) {
      console.debug(
        '[emailNotifications] notification skipped:',
        (data as any).reason,
      );
      return { ok: false, reason: (data as any).reason ?? 'skipped' };
    }
    return { ok: true };
  } catch (err) {
    console.debug('[emailNotifications] send exception (ignored)', err);
    return { ok: false, reason: 'exception' };
  }
};

// ────────────────────────────────────────────────────────────────────
// Convenience wrappers — used by FamilyGraphContext, ConnectUserModal,
// MediaUploadModal and friends. Every wrapper is fire-and-forget safe.
// ────────────────────────────────────────────────────────────────────

/**
 * Notify the whole family tree that a new memory / photo / video /
 * story / life event was just added.
 */
export const notifyFamilyOfMemoryUpload = (args: {
  ownerUserId: string;
  actorUserId: string;
  actorName?: string | null;
  memoryKind: 'photo' | 'video' | 'memory' | 'story' | 'life_event';
  memoryTitle?: string | null;
  aboutMemberName?: string | null;
}) =>
  sendFamilyNotification({
    type: 'memory_uploaded',
    ownerUserId: args.ownerUserId,
    actorUserId: args.actorUserId,
    actorName: args.actorName ?? null,
    payload: {
      memoryKind: args.memoryKind,
      memoryTitle: args.memoryTitle ?? null,
      aboutMemberName: args.aboutMemberName ?? null,
    },
  });

/**
 * Notify the family tree owner + connected relatives that a new family
 * member just joined / claimed a profile.
 */
export const notifyFamilyOfMemberJoined = (args: {
  ownerUserId: string;
  actorUserId: string;
  newMemberName: string;
  inviterUserId?: string | null;
}) =>
  sendFamilyNotification({
    type: 'member_joined',
    ownerUserId: args.ownerUserId,
    actorUserId: args.actorUserId,
    actorName: args.newMemberName,
    payload: {
      newMemberName: args.newMemberName,
      inviterUserId: args.inviterUserId ?? null,
    },
  });

// ────────────────────────────────────────────────────────────────────
// Legacy single-recipient wrappers — preserved so existing imports keep
// working. These call the same edge function in single-recipient mode.
// ────────────────────────────────────────────────────────────────────

export const notifyMemberJoined = (args: BaseRecipient & {
  newMemberName: string;
  inviterName?: string | null;
}) =>
  sendFamilyNotification({
    type: 'member_joined',
    recipientUserId: args.recipientUserId,
    recipientEmail: args.recipientEmail,
    recipientName: args.recipientName,
    payload: {
      newMemberName: args.newMemberName,
      inviterName: args.inviterName ?? null,
    },
  });

export const notifyMemoryUploaded = (args: BaseRecipient & {
  uploaderName: string;
  memoryKind: 'photo' | 'video' | 'memory';
  memoryTitle?: string | null;
}) =>
  sendFamilyNotification({
    type: 'memory_uploaded',
    recipientUserId: args.recipientUserId,
    recipientEmail: args.recipientEmail,
    recipientName: args.recipientName,
    payload: {
      uploaderName: args.uploaderName,
      memoryKind: args.memoryKind,
      memoryTitle: args.memoryTitle ?? null,
    },
  });

export const notifyMemoryComment = (args: BaseRecipient & {
  commenterName: string;
  memoryTitle?: string | null;
  excerpt?: string | null;
}) =>
  sendFamilyNotification({
    type: 'memory_comment',
    recipientUserId: args.recipientUserId,
    recipientEmail: args.recipientEmail,
    recipientName: args.recipientName,
    payload: {
      commenterName: args.commenterName,
      memoryTitle: args.memoryTitle ?? null,
      excerpt: args.excerpt ? args.excerpt.slice(0, 240) : null,
    },
  });

export const notifyMemoryReaction = (args: BaseRecipient & {
  reactorName: string;
  reaction: string;
  memoryTitle?: string | null;
}) =>
  sendFamilyNotification({
    type: 'memory_reaction',
    recipientUserId: args.recipientUserId,
    recipientEmail: args.recipientEmail,
    recipientName: args.recipientName,
    payload: {
      reactorName: args.reactorName,
      reaction: args.reaction,
      memoryTitle: args.memoryTitle ?? null,
    },
  });

export const notifyBirthdayUpcoming = (args: BaseRecipient & {
  personName: string;
  daysUntil: number;
  birthdayLabel?: string | null;
}) =>
  sendFamilyNotification({
    type: 'birthday_reminder',
    recipientUserId: args.recipientUserId,
    recipientEmail: args.recipientEmail,
    recipientName: args.recipientName,
    payload: {
      personName: args.personName,
      daysUntil: args.daysUntil,
      birthdayLabel: args.birthdayLabel ?? null,
    },
  });

export const notifyProfileClaimed = (args: BaseRecipient & {
  claimedProfileName: string;
  claimerName?: string | null;
}) =>
  sendFamilyNotification({
    type: 'profile_claimed',
    recipientUserId: args.recipientUserId,
    recipientEmail: args.recipientEmail,
    recipientName: args.recipientName,
    payload: {
      claimedProfileName: args.claimedProfileName,
      claimerName: args.claimerName ?? null,
    },
  });

// ────────────────────────────────────────────────────────────────────
// Weekly digest (kept for the EmailPreferences "send sample" button).
// The send-weekly-digest function is optional; failures are silent.
// ────────────────────────────────────────────────────────────────────

export interface WeeklyDigestSummary {
  newMemoriesCount: number;
  newMembersCount: number;
  newCommentsCount: number;
  upcomingBirthdays: Array<{ name: string; daysUntil: number }>;
  suggestedAction: 'invite_relative' | 'add_memory';
  recentMemoryTitles?: string[];
}

export const sendWeeklyDigest = async (args: {
  recipientUserId?: string | null;
  recipientEmail?: string | null;
  recipientName?: string | null;
  summary: WeeklyDigestSummary;
}): Promise<{ ok: boolean; skipped?: boolean; reason?: string }> => {
  const userId = args.recipientUserId ?? null;
  if (!isChannelEnabled(userId, 'weekly_digest')) {
    return { ok: false, skipped: true, reason: 'channel_disabled' };
  }
  if (!args.recipientUserId && !args.recipientEmail) {
    return { ok: false, skipped: true, reason: 'no_recipient' };
  }
  try {
    const { error } = await supabase.functions.invoke('send-weekly-digest', {
      body: {
        recipientUserId: args.recipientUserId ?? null,
        recipientEmail: args.recipientEmail ?? null,
        recipientName: args.recipientName ?? null,
        summary: args.summary,
        subject: 'Your KinsCircle family update',
      },
    });
    if (error) {
      console.debug(
        '[emailNotifications] send-weekly-digest unavailable:',
        error.message,
      );
      return { ok: false, reason: 'edge_function_error' };
    }
    return { ok: true };
  } catch (err) {
    console.debug('[emailNotifications] digest exception (ignored)', err);
    return { ok: false, reason: 'exception' };
  }
};
